import { QueryClientContext } from '@tanstack/react-query';
import { useContext, useRef, useState, type ImgHTMLAttributes } from 'react';

import { academyAssetPath, refetchAcademyAssetOwners } from '@/lib/academyAssets';
import { BASE_URL } from '@/lib/api';

type AcademyAssetImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'onError'> & {
  assetKey: string;
};

/**
 * An Academy figure drawn from its signed URL (SIR-HYG-03). A sitting can outlast a signature,
 * so when the image fails to load it refetches the owning question query once (fresh signed
 * paths) and draws again. It retries only once per mount, so a missing file never loops.
 * Outside a QueryClientProvider (static previews) it simply shows the first URL.
 */
export function AcademyAssetImage({ assetKey, alt, ...imgProps }: AcademyAssetImageProps) {
  const client = useContext(QueryClientContext);
  const retried = useRef(false);
  const [, setGeneration] = useState(0);

  const onError = () => {
    if (retried.current || !client) return;
    retried.current = true;
    void refetchAcademyAssetOwners(client, assetKey).then(() =>
      setGeneration((generation) => generation + 1),
    );
  };

  return (
    <img
      {...imgProps}
      alt={alt}
      src={`${BASE_URL}${academyAssetPath(assetKey)}`}
      onError={onError}
    />
  );
}
